import type { FiltroDeCampo, ItemDoFunilNoCrm } from "@base/contracts";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { paraDataIso, type Intervalo } from "../../../shared/periodo/periodos";
import type { DashboardGateway } from "../infrastructure/dashboard.gateway";

const Contexto = createContext<DashboardGateway | null>(null);

export function ProvedorDeDashboard({
  gateway,
  children,
}: {
  readonly gateway: DashboardGateway;
  readonly children: ReactNode;
}) {
  return <Contexto.Provider value={gateway}>{children}</Contexto.Provider>;
}

function useDashboardGateway(): DashboardGateway {
  const gateway = useContext(Contexto);
  if (!gateway) throw new Error("useDashboardGateway precisa estar dentro de <ProvedorDeDashboard>.");
  return gateway;
}

/** Resumo do Dashboard do cliente no periodo. A chave inclui cliente e datas. */
export function useResumoDoDashboard(clienteId: string, periodo: Intervalo, filtros: readonly FiltroDeCampo[] = []) {
  const gateway = useDashboardGateway();
  return useQuery({
    queryKey: ["dashboard", clienteId, paraDataIso(periodo.inicio), paraDataIso(periodo.fim), filtros],
    queryFn: () => gateway.obter(clienteId, periodo, filtros),
    // Trocar de periodo mantem os numeros antigos na tela ate os novos chegarem,
    // em vez de piscar o esqueleto a cada clique.
    placeholderData: (anterior) => anterior,
  });
}

/** Oportunidades da etapa escolhida, no mesmo periodo e filtro do resumo. */
export function useOportunidadesDoDashboard(
  clienteId: string,
  periodo: Intervalo,
  filtros: readonly FiltroDeCampo[],
  etapa: ItemDoFunilNoCrm,
  habilitado: boolean,
) {
  const gateway = useDashboardGateway();
  return useQuery({
    queryKey: ["dashboard", clienteId, "oportunidades", paraDataIso(periodo.inicio), paraDataIso(periodo.fim), filtros, etapa],
    queryFn: () => gateway.oportunidades(clienteId, periodo, filtros, etapa),
    enabled: habilitado,
  });
}

/** Contatos com lead no periodo e o historico de oportunidades. */
export function useHistoricoDeContatosDoDashboard(
  clienteId: string,
  periodo: Intervalo,
  filtros: readonly FiltroDeCampo[],
  habilitado: boolean,
) {
  const gateway = useDashboardGateway();
  return useQuery({
    queryKey: ["dashboard", clienteId, "contatos", paraDataIso(periodo.inicio), paraDataIso(periodo.fim), filtros],
    queryFn: () => gateway.historicoDeContatos(clienteId, periodo, filtros),
    enabled: habilitado,
  });
}

/** Campos do Kommo que filtram o Dashboard. So busca com dados reais. */
export function useOpcoesDeFiltro(clienteId: string, habilitado: boolean) {
  const gateway = useDashboardGateway();
  return useQuery({
    queryKey: ["dashboard", clienteId, "filtros"],
    queryFn: () => gateway.opcoesDeFiltro(clienteId),
    enabled: habilitado,
  });
}
